import { Timestamp } from "firebase/firestore";
import { EntryCollection } from "@/types/entryCollection";
import { BooleanEntry, ImageMeasurementEntry } from "@/types/entry";
import { EntryConfig } from "@/types/entryConfig";
import {
  ImageMeasurementMetric,
  StandCountMetric,
  RowPlantedStandCountMetric,
  convertDescriptorValueForDisplay,
} from "@/types/measurementMetrics";
import { computeWeightedCountPerM2, computeWeightedStandCountParts } from "@/lib/standCountUtils";
import { getMostRecentStoppedTimerMs, getStoppedTimerMs } from "@/utils/timerValueUtils";
import { getUnboundDescriptors, type MeasurementDescriptor } from "@shared/types/measurementDescriptors";

/**
 * Result type for aggregated values that includes error state information.
 * Used when weighted averaging may fail due to missing count data.
 */
export interface AggregatedValueResult {
  /** The calculated value, or null if unable to calculate */
  value: number | null;
  /** Whether an error occurred during calculation */
  hasError: boolean;
  /** Reason for the error, if any */
  errorReason?: "no_valid_counts" | "no_entries";
  /** Total number of entries found */
  totalEntries: number;
  /** Number of entries with valid counts for weighted averaging */
  validWeightedEntries: number;
}

/**
 * Helper to get all entries from a map.
 */
function getAllEntriesFromMap<T>(entriesMap: Record<string, T> | undefined): T[] {
  if (!entriesMap) return [];
  return Object.values(entriesMap);
}

/**
 * Finds the first sample slot that actually carries a boolean value.
 *
 * Empty-but-present slots are routine: iOS pre-creates slots 1..minNumSamples via
 * `addEmptySampleNonBlocking`, and Clear/Skip nulls `val` while leaving the slot
 * behind. Searching for the first non-null *entry* would therefore stop at an empty
 * slot and report "no value" while a later sample holds a real answer (GLE-84).
 * Sample keys are integer-like, so `Object.values` iterates in ascending sample order.
 */
function findFirstBooleanEntryWithValue(
  entriesMap: Record<string, BooleanEntry> | undefined
): BooleanEntry | undefined {
  if (!entriesMap) return undefined;
  return Object.values(entriesMap).find((entry) => entry?.val != null);
}

/**
 * Helper to get the entryConfigId from a collection.
 */
function getEntryConfigId(collection: EntryCollection): string {
  return typeof collection.entryConfigRef === "string" ? collection.entryConfigRef : collection.entryConfigRef.id;
}

function getImageMeasurementDescriptor(
  entryConfig: EntryConfig | undefined,
  metric: ImageMeasurementMetric
): MeasurementDescriptor | undefined {
  return getUnboundDescriptors(entryConfig).find((descriptor) => descriptor.key === metric);
}

function getDescriptorMeasurementValue(entry: ImageMeasurementEntry, descriptor: MeasurementDescriptor): number | null {
  const value =
    descriptor.aggregation === "mean"
      ? entry.additionalMeasurements?.[descriptor.key]?.avg
      : entry.additionalMeasurements?.[descriptor.key]?.total;

  return value != null ? convertDescriptorValueForDisplay(value, descriptor) : null;
}

function getEntryMapForCollection(collection: EntryCollection): Record<string, unknown> | undefined {
  switch (collection.type) {
    case "text":
      return collection.textEntries;
    case "number":
      return collection.numberEntries;
    case "category":
      return collection.categoryEntries;
    case "date":
      return collection.dateEntries;
    case "transcription":
      return collection.transcriptionEntries;
    case "photo":
      return collection.photoEntries;
    case "boolean":
      return collection.booleanEntries;
    case "percentage":
      return collection.percentageEntries;
    case "leafAreaIndex":
      return collection.leafAreaIndexEntries;
    case "ruler":
      return collection.rulerEntries;
    case "imageMeasurement":
      return collection.imageMeasurementEntries;
    case "standCountMeasurement":
      return collection.standCountMeasurementEntries;
    case "rowPlantedStandCount":
      return collection.rowPlantedStandCountEntries;
    case "timer": {
      // A timer slot counts as "collected" only once it is stopped
      // (elapsedMs > 0). Idle (elapsedMs absent/0) and running
      // (startedAtEpochMs only) slots have no final value, so filter them out
      // before the generic non-null counter — otherwise a never-stopped
      // stopwatch would mark the cell collected. Mirrors the display/export
      // predicate so all timer surfaces agree.
      if (!collection.timerEntries) return undefined;
      return Object.fromEntries(
        Object.entries(collection.timerEntries).filter(([, entry]) => getStoppedTimerMs(entry) != null)
      );
    }
    default:
      return undefined;
  }
}

export function getCollectedEntryCount(entryCollections: EntryCollection[], entryConfigId: string): number {
  return entryCollections
    .filter((collection) => getEntryConfigId(collection) === entryConfigId)
    .reduce((count, collection) => {
      const entryMap = getEntryMapForCollection(collection);
      if (!entryMap) return count;
      return count + Object.values(entryMap).filter((entry) => entry != null).length;
    }, 0);
}

/**
 * Calculates a weighted average with error status handling.
 * This is the core logic used by all measurement type aggregations.
 *
 * @param entries - Array of entries to aggregate
 * @param getMetricValue - Function to extract the metric value from an entry
 * @param getWeight - Function to extract the weight (count) from an entry
 * @returns AggregatedValueResult with value and error status
 */
function calculateWeightedAverageWithStatus<T>(
  entries: T[],
  getMetricValue: (entry: T) => number | null | undefined,
  getWeight: (entry: T) => number | null | undefined
): AggregatedValueResult {
  if (entries.length === 0) {
    return {
      value: null,
      hasError: false,
      errorReason: "no_entries",
      totalEntries: 0,
      validWeightedEntries: 0,
    };
  }

  // Entries that have the metric value (even without valid weight)
  const entriesWithMetric = entries.filter((e) => getMetricValue(e) != null);

  // Entries that have both the metric and valid weight for weighting
  const validEntries = entries.filter((e) => {
    const weight = getWeight(e);
    return getMetricValue(e) != null && weight != null && weight > 0;
  });

  // Single entry case: return value directly without weighting
  if (entriesWithMetric.length === 1) {
    return {
      value: getMetricValue(entriesWithMetric[0])!,
      hasError: false,
      totalEntries: entries.length,
      validWeightedEntries: validEntries.length,
    };
  }

  // Multiple entries with no valid weights: error state
  if (entriesWithMetric.length > 1 && validEntries.length === 0) {
    return {
      value: null,
      hasError: true,
      errorReason: "no_valid_counts",
      totalEntries: entries.length,
      validWeightedEntries: 0,
    };
  }

  // Multiple entries with at least some valid weights: calculate weighted average
  if (validEntries.length > 0) {
    const totalWeight = validEntries.reduce((sum, e) => sum + getWeight(e)!, 0);
    const weightedSum = validEntries.reduce((sum, e) => sum + getMetricValue(e)! * getWeight(e)!, 0);
    return {
      value: totalWeight > 0 ? weightedSum / totalWeight : null,
      hasError: false,
      totalEntries: entries.length,
      validWeightedEntries: validEntries.length,
    };
  }

  // No entries with metric value
  return {
    value: null,
    hasError: false,
    totalEntries: entries.length,
    validWeightedEntries: 0,
  };
}

/**
 * Extracts and aggregates imageMeasurement values based on the selected metric.
 * Aggregates across all matching entry collections (multiple users).
 * - count: Sum of all counts
 * - length/width: Weighted average by count (converted to cm)
 * - weight: Sum of all weights
 */
export function getImageMeasurementValue(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  metric: ImageMeasurementMetric,
  entryConfig?: EntryConfig
): number | null {
  // Filter EntryCollections to only those matching the selected EntryConfig
  const matchingCollections = entryCollections.filter((entryCollection) => {
    const collectionEntryConfigId =
      typeof entryCollection.entryConfigRef === "string"
        ? entryCollection.entryConfigRef
        : entryCollection.entryConfigRef.id;
    return collectionEntryConfigId === entryConfigId && entryCollection.type === "imageMeasurement";
  });

  if (matchingCollections.length === 0) {
    return null;
  }

  // Helper to get all entries from the map
  const getAllEntries = <T>(entriesMap: Record<string, T> | undefined): T[] => {
    if (!entriesMap) return [];
    return Object.values(entriesMap);
  };

  // Collect all entries from all matching collections
  const allEntries = matchingCollections.flatMap((collection) => getAllEntries(collection.imageMeasurementEntries));

  if (allEntries.length === 0) return null;

  switch (metric) {
    case "count": {
      // Sum all counts
      const counts = allEntries.map((e) => e.objectCount).filter((v) => v != null) as number[];
      if (counts.length === 0) return null;
      return counts.reduce((sum, count) => sum + count, 0);
    }

    case "length":
    case "width": {
      // Weighted average by objectCount
      const field = metric === "length" ? "avgLengthMeters" : "avgWidthMeters";

      // Filter entries that have both the metric and count
      const validEntries = allEntries.filter((e) => e[field] != null && e.objectCount != null && e.objectCount > 0);

      if (validEntries.length === 0) return null;

      const totalCount = validEntries.reduce((sum, e) => sum + e.objectCount!, 0);
      const weightedSum = validEntries.reduce((sum, e) => sum + e[field]! * e.objectCount!, 0);

      // Convert meters to centimeters (multiply by 100)
      return totalCount > 0 ? (weightedSum / totalCount) * 100 : null;
    }

    case "weight": {
      // Sum all weights
      const weights = allEntries.map((e) => e.totalWeightGrams).filter((v) => v != null) as number[];
      if (weights.length === 0) return null;
      return weights.reduce((sum, weight) => sum + weight, 0);
    }

    default: {
      const descriptor = getImageMeasurementDescriptor(entryConfig, metric);
      if (!descriptor) return null;

      if (descriptor.aggregation === "sum") {
        const values = allEntries
          .map((entry) => getDescriptorMeasurementValue(entry, descriptor))
          .filter((value): value is number => value != null);
        return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) : null;
      }

      const validEntries = allEntries.filter((entry) => {
        const value = getDescriptorMeasurementValue(entry, descriptor);
        return value != null && entry.objectCount != null && entry.objectCount > 0;
      });
      if (validEntries.length === 0) return null;

      const totalCount = validEntries.reduce((sum, entry) => sum + entry.objectCount!, 0);
      const weightedSum = validEntries.reduce(
        (sum, entry) => sum + getDescriptorMeasurementValue(entry, descriptor)! * entry.objectCount!,
        0
      );
      return totalCount > 0 ? weightedSum / totalCount : null;
    }
  }
}

/**
 * Extracts and aggregates imageMeasurement values with status information.
 * Returns error state when multiple entries exist but none have valid counts for weighted averaging.
 * This is only applicable for length/width metrics that require weighted averaging.
 */
export function getImageMeasurementValueWithStatus(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  metric: ImageMeasurementMetric,
  entryConfig?: EntryConfig
): AggregatedValueResult {
  const matchingCollections = entryCollections.filter(
    (c) => getEntryConfigId(c) === entryConfigId && c.type === "imageMeasurement"
  );

  if (matchingCollections.length === 0) {
    return { value: null, hasError: false, totalEntries: 0, validWeightedEntries: 0 };
  }

  const allEntries = matchingCollections.flatMap((c) => getAllEntriesFromMap(c.imageMeasurementEntries));

  if (allEntries.length === 0) {
    return { value: null, hasError: false, errorReason: "no_entries", totalEntries: 0, validWeightedEntries: 0 };
  }

  // For count and weight, we sum - no weighted averaging needed
  if (metric === "count") {
    const counts = allEntries.map((e) => e.objectCount).filter((v) => v != null) as number[];
    return {
      value: counts.length > 0 ? counts.reduce((sum, count) => sum + count, 0) : null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: counts.length,
    };
  }

  if (metric === "weight") {
    const weights = allEntries.map((e) => e.totalWeightGrams).filter((v) => v != null) as number[];
    return {
      value: weights.length > 0 ? weights.reduce((sum, weight) => sum + weight, 0) : null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: weights.length,
    };
  }

  const descriptor = getImageMeasurementDescriptor(entryConfig, metric);
  if (descriptor) {
    if (descriptor.aggregation === "sum") {
      const values = allEntries
        .map((entry) => getDescriptorMeasurementValue(entry, descriptor))
        .filter((value): value is number => value != null);
      return {
        value: values.length > 0 ? values.reduce((sum, value) => sum + value, 0) : null,
        hasError: false,
        totalEntries: allEntries.length,
        validWeightedEntries: values.length,
      };
    }

    return calculateWeightedAverageWithStatus(
      allEntries,
      (entry) => getDescriptorMeasurementValue(entry, descriptor),
      (entry) => entry.objectCount
    );
  }

  if (metric !== "length" && metric !== "width") {
    return {
      value: null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: 0,
    };
  }

  // For length/width, use weighted averaging by objectCount (convert meters to cm)
  const field = metric === "length" ? "avgLengthMeters" : "avgWidthMeters";
  const result = calculateWeightedAverageWithStatus(
    allEntries,
    (e) => (e[field] != null ? e[field]! * 100 : null), // Convert meters to cm
    (e) => e.objectCount
  );
  return result;
}

/**
 * Extracts and aggregates standCountMeasurement values based on the selected metric.
 * Aggregates across all matching entry collections (multiple users).
 * - countPerM2: Simple average (normalized metric)
 * - rawCount: Sum of all counts
 * - avgLength/avgWidth: Weighted average by objectCount (converted to cm)
 * - totalWeight: Sum of all weights
 */
export function getStandCountValue(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  metric: StandCountMetric
): number | null {
  // Filter EntryCollections to only those matching the selected EntryConfig
  const matchingCollections = entryCollections.filter((entryCollection) => {
    const collectionEntryConfigId =
      typeof entryCollection.entryConfigRef === "string"
        ? entryCollection.entryConfigRef
        : entryCollection.entryConfigRef.id;
    return collectionEntryConfigId === entryConfigId && entryCollection.type === "standCountMeasurement";
  });

  if (matchingCollections.length === 0) {
    return null;
  }

  // Helper to get all entries from the map
  const getAllEntries = <T>(entriesMap: Record<string, T> | undefined): T[] => {
    if (!entriesMap) return [];
    return Object.values(entriesMap);
  };

  // Helper to calculate average
  const calculateAverage = (values: number[]): number | null => {
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  // Collect all entries from all matching collections
  const allEntries = matchingCollections.flatMap((collection) =>
    getAllEntries(collection.standCountMeasurementEntries)
  );

  if (allEntries.length === 0) return null;

  switch (metric) {
    case "countPerM2": {
      const weightedCountPerM2 = computeWeightedCountPerM2(
        allEntries.map((entry) => ({
          countPerSquareMeter: entry.countPerSquareMeter,
          objectCount: entry.imageMeasurement?.objectCount,
          boxLengthM: entry.standCountBoxLengthMeters,
          boxWidthM: entry.standCountBoxWidthMeters,
        }))
      );

      if (weightedCountPerM2 != null) {
        return weightedCountPerM2;
      }

      // Fallback to simple average for legacy data without raw measurements
      const countPerM2Values = allEntries.map((e) => e.countPerSquareMeter).filter((v) => v != null) as number[];
      return calculateAverage(countPerM2Values);
    }

    case "rawCount": {
      // Sum all raw counts
      const rawCounts = allEntries.map((e) => e.imageMeasurement?.objectCount).filter((v) => v != null) as number[];
      if (rawCounts.length === 0) return null;
      return rawCounts.reduce((sum, count) => sum + count, 0);
    }

    case "avgLength":
    case "avgWidth": {
      // Weighted average by objectCount
      const field = metric === "avgLength" ? "avgLengthMeters" : "avgWidthMeters";

      // Filter entries that have both the metric and count
      const validEntries = allEntries.filter(
        (e) =>
          e.imageMeasurement?.[field] != null &&
          e.imageMeasurement?.objectCount != null &&
          e.imageMeasurement.objectCount > 0
      );

      if (validEntries.length === 0) return null;

      const totalCount = validEntries.reduce((sum, e) => sum + e.imageMeasurement!.objectCount!, 0);
      const weightedSum = validEntries.reduce(
        (sum, e) => sum + e.imageMeasurement![field]! * e.imageMeasurement!.objectCount!,
        0
      );

      // Convert meters to centimeters (multiply by 100)
      return totalCount > 0 ? (weightedSum / totalCount) * 100 : null;
    }

    case "totalWeight": {
      // Sum all weights
      const weights = allEntries.map((e) => e.imageMeasurement?.totalWeightGrams).filter((v) => v != null) as number[];
      if (weights.length === 0) return null;
      return weights.reduce((sum, weight) => sum + weight, 0);
    }

    default:
      return null;
  }
}

/**
 * Extracts and aggregates standCountMeasurement values with status information.
 * Returns error state when multiple entries exist but none have valid counts for weighted averaging.
 * This is only applicable for avgLength/avgWidth metrics that require weighted averaging.
 */
export function getStandCountValueWithStatus(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  metric: StandCountMetric
): AggregatedValueResult {
  const matchingCollections = entryCollections.filter(
    (c) => getEntryConfigId(c) === entryConfigId && c.type === "standCountMeasurement"
  );

  if (matchingCollections.length === 0) {
    return { value: null, hasError: false, totalEntries: 0, validWeightedEntries: 0 };
  }

  const allEntries = matchingCollections.flatMap((c) => getAllEntriesFromMap(c.standCountMeasurementEntries));

  if (allEntries.length === 0) {
    return { value: null, hasError: false, errorReason: "no_entries", totalEntries: 0, validWeightedEntries: 0 };
  }

  // countPerM2: recalculate from raw data for weighted accuracy
  if (metric === "countPerM2") {
    const weightedParts = computeWeightedStandCountParts(
      allEntries.map((entry) => ({
        countPerSquareMeter: entry.countPerSquareMeter,
        objectCount: entry.imageMeasurement?.objectCount,
        boxLengthM: entry.standCountBoxLengthMeters,
        boxWidthM: entry.standCountBoxWidthMeters,
      }))
    );

    if (weightedParts.validEntries > 0) {
      return {
        value: weightedParts.totalArea > 0 ? weightedParts.totalCount / weightedParts.totalArea : null,
        hasError: false,
        totalEntries: allEntries.length,
        validWeightedEntries: weightedParts.validEntries,
      };
    }

    // Final fallback: simple average for legacy data without any raw measurements
    const countPerM2Values = allEntries.map((e) => e.countPerSquareMeter).filter((v) => v != null) as number[];
    return {
      value:
        countPerM2Values.length > 0 ? countPerM2Values.reduce((sum, v) => sum + v, 0) / countPerM2Values.length : null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: countPerM2Values.length,
    };
  }

  // rawCount: sum all raw counts
  if (metric === "rawCount") {
    const rawCounts = allEntries.map((e) => e.imageMeasurement?.objectCount).filter((v) => v != null) as number[];
    return {
      value: rawCounts.length > 0 ? rawCounts.reduce((sum, count) => sum + count, 0) : null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: rawCounts.length,
    };
  }

  // totalWeight: sum all weights
  if (metric === "totalWeight") {
    const weights = allEntries.map((e) => e.imageMeasurement?.totalWeightGrams).filter((v) => v != null) as number[];
    return {
      value: weights.length > 0 ? weights.reduce((sum, weight) => sum + weight, 0) : null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: weights.length,
    };
  }

  // avgLength/avgWidth: weighted averaging by objectCount (convert meters to cm)
  const field = metric === "avgLength" ? "avgLengthMeters" : "avgWidthMeters";
  return calculateWeightedAverageWithStatus(
    allEntries,
    (e) => (e.imageMeasurement?.[field] != null ? e.imageMeasurement[field]! * 100 : null),
    (e) => e.imageMeasurement?.objectCount
  );
}

/** Sum plantCount across row-planted entries; null when no entry has a count. */
function sumPlantCounts(entries: Array<{ plantCount?: number }>): number | null {
  let total = 0;
  let found = false;
  for (const e of entries) {
    if (e.plantCount == null) continue;
    total += e.plantCount;
    found = true;
  }
  return found ? total : null;
}

/**
 * Extracts and aggregates rowPlantedStandCount values based on the selected metric.
 * Aggregates across all matching entry collections (multiple users).
 * - plantsPerMeter: Average plants per meter
 * - averageSpacing: Average spacing (converted to cm)
 * - averageDiameter: Average length (converted to cm)
 * - averageWidth: Average width (converted to cm)
 * - averageGreenness: Average VARI greenness index
 */
export function getRowPlantedStandCountValue(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  metric: RowPlantedStandCountMetric
): number | null {
  // Filter EntryCollections to only those matching the selected EntryConfig
  const matchingCollections = entryCollections.filter((entryCollection) => {
    const collectionEntryConfigId =
      typeof entryCollection.entryConfigRef === "string"
        ? entryCollection.entryConfigRef
        : entryCollection.entryConfigRef.id;
    return collectionEntryConfigId === entryConfigId && entryCollection.type === "rowPlantedStandCount";
  });

  if (matchingCollections.length === 0) {
    return null;
  }

  // Helper to get all entries from the map
  const getAllEntries = <T>(entriesMap: Record<string, T> | undefined): T[] => {
    if (!entriesMap) return [];
    return Object.values(entriesMap);
  };

  // Helper to calculate average
  const calculateAverage = (values: number[]): number | null => {
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  // Collect all entries from all matching collections
  const allEntries = matchingCollections.flatMap((collection) => getAllEntries(collection.rowPlantedStandCountEntries));

  if (allEntries.length === 0) return null;

  // Helper to calculate weighted average by plantCount
  const calculateWeightedAverage = (
    getValue: (e: (typeof allEntries)[0]) => number | null | undefined
  ): number | null => {
    // Filter entries with valid metric value and plantCount > 0
    const validEntries = allEntries.filter((e) => getValue(e) != null && e.plantCount != null && e.plantCount > 0);

    if (validEntries.length > 0) {
      const totalPlants = validEntries.reduce((sum, e) => sum + e.plantCount!, 0);
      const weightedSum = validEntries.reduce((sum, e) => sum + getValue(e)! * e.plantCount!, 0);
      return totalPlants > 0 ? weightedSum / totalPlants : null;
    }

    // Fallback to simple average if plantCount unavailable
    const values = allEntries.map((e) => getValue(e)).filter((v) => v != null) as number[];
    return calculateAverage(values);
  };

  switch (metric) {
    case "plantsPerMeter": {
      // Try to recalculate from raw data (sum of plants / sum of distances) for weighted accuracy
      const entriesWithRawData = allEntries.filter(
        (e) => e.plantCount != null && e.walkDistanceMeters != null && e.walkDistanceMeters > 0
      );

      if (entriesWithRawData.length > 0) {
        const totalPlants = entriesWithRawData.reduce((sum, e) => sum + e.plantCount!, 0);
        const totalDistance = entriesWithRawData.reduce((sum, e) => sum + e.walkDistanceMeters!, 0);
        return totalDistance > 0 ? totalPlants / totalDistance : null;
      }

      // Fallback to simple average for legacy data
      const ppmValues = allEntries.map((e) => e.plantsPerMeter).filter((v) => v != null) as number[];
      return calculateAverage(ppmValues);
    }

    case "numberPlants":
      // Tally semantics — sum plantCount across all entries in the plot.
      return sumPlantCounts(allEntries);

    case "averageSpacing":
      // Weighted average spacing by plantCount, converted from meters to cm
      return calculateWeightedAverage((e) => (e.averageSpacingMeters != null ? e.averageSpacingMeters * 100 : null));

    case "averageDiameter":
      // Weighted average length by plantCount, converted from meters to cm
      return calculateWeightedAverage((e) => (e.averageDiameterMeters != null ? e.averageDiameterMeters * 100 : null));

    case "averageWidth":
      // Weighted average width by plantCount, converted from meters to cm
      return calculateWeightedAverage((e) => (e.averageWidthMeters != null ? e.averageWidthMeters * 100 : null));

    case "averageGreenness":
      // Weighted average VARI greenness by plantCount
      return calculateWeightedAverage((e) => e.averageVari);

    default:
      return null;
  }
}

/**
 * Extracts and aggregates rowPlantedStandCount values with status information.
 * Returns error state when multiple entries exist but none have valid counts for weighted averaging.
 * This is only applicable for averageSpacing/averageDiameter/averageGreenness metrics.
 */
export function getRowPlantedStandCountValueWithStatus(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  metric: RowPlantedStandCountMetric
): AggregatedValueResult {
  const matchingCollections = entryCollections.filter(
    (c) => getEntryConfigId(c) === entryConfigId && c.type === "rowPlantedStandCount"
  );

  if (matchingCollections.length === 0) {
    return { value: null, hasError: false, totalEntries: 0, validWeightedEntries: 0 };
  }

  const allEntries = matchingCollections.flatMap((c) => getAllEntriesFromMap(c.rowPlantedStandCountEntries));

  if (allEntries.length === 0) {
    return { value: null, hasError: false, errorReason: "no_entries", totalEntries: 0, validWeightedEntries: 0 };
  }

  if (metric === "numberPlants") {
    const total = sumPlantCounts(allEntries);
    const countsLength = allEntries.filter((e) => e.plantCount != null).length;
    return {
      value: total,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: countsLength,
    };
  }

  // plantsPerMeter: recalculate from raw data for weighted accuracy
  if (metric === "plantsPerMeter") {
    const entriesWithRawData = allEntries.filter(
      (e) => e.plantCount != null && e.walkDistanceMeters != null && e.walkDistanceMeters > 0
    );

    if (entriesWithRawData.length > 0) {
      const totalPlants = entriesWithRawData.reduce((sum, e) => sum + e.plantCount!, 0);
      const totalDistance = entriesWithRawData.reduce((sum, e) => sum + e.walkDistanceMeters!, 0);
      return {
        value: totalDistance > 0 ? totalPlants / totalDistance : null,
        hasError: false,
        totalEntries: allEntries.length,
        validWeightedEntries: entriesWithRawData.length,
      };
    }

    // Fallback to simple average
    const ppmValues = allEntries.map((e) => e.plantsPerMeter).filter((v) => v != null) as number[];
    return {
      value: ppmValues.length > 0 ? ppmValues.reduce((sum, v) => sum + v, 0) / ppmValues.length : null,
      hasError: false,
      totalEntries: allEntries.length,
      validWeightedEntries: ppmValues.length,
    };
  }

  // For averageSpacing/averageDiameter/averageWidth/averageGreenness: weighted averaging by plantCount
  type MetricGetter = (e: (typeof allEntries)[0]) => number | null | undefined;
  let getValue: MetricGetter;

  switch (metric) {
    case "averageSpacing":
      getValue = (e) => (e.averageSpacingMeters != null ? e.averageSpacingMeters * 100 : null);
      break;
    case "averageDiameter":
      getValue = (e) => (e.averageDiameterMeters != null ? e.averageDiameterMeters * 100 : null);
      break;
    case "averageWidth":
      getValue = (e) => (e.averageWidthMeters != null ? e.averageWidthMeters * 100 : null);
      break;
    case "averageGreenness":
      getValue = (e) => e.averageVari;
      break;
    default:
      return { value: null, hasError: false, totalEntries: allEntries.length, validWeightedEntries: 0 };
  }

  return calculateWeightedAverageWithStatus(allEntries, getValue, (e) => e.plantCount);
}

/**
 * Extracts a display value from EntryCollections based on the specified trait ID.
 * Aggregates across all matching entry collections (multiple users).
 * - Numerical types: Average of all values from all collections
 * - Date type: Average date across all collections
 * - Other non-numerical types (text, category, boolean): Most recent value based on collection timestamp
 * - imageMeasurement/standCountMeasurement: Delegated to specialized functions
 * - Photo types: Returns "photo" sentinel if any photos exist, null otherwise
 */
export function getEntryDisplayValue(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  imageMeasurementMetric?: ImageMeasurementMetric,
  standCountMetric?: StandCountMetric,
  rowPlantedStandCountMetric?: RowPlantedStandCountMetric,
  entryConfig?: EntryConfig
): unknown | null {
  // Filter EntryCollections to only those matching the selected EntryConfig
  const matchingCollections = entryCollections.filter((entryCollection) => {
    const collectionEntryConfigId =
      typeof entryCollection.entryConfigRef === "string"
        ? entryCollection.entryConfigRef
        : entryCollection.entryConfigRef.id;
    return collectionEntryConfigId === entryConfigId;
  });

  if (matchingCollections.length === 0) {
    return null;
  }

  const entryType = matchingCollections[0].type;

  // For imageMeasurement types, use the specialized function with metric selection.
  // 'grade' is categorical and computed elsewhere — return null here so callers
  // that expect a numeric/scalar value just see "no value".
  if (entryType === "imageMeasurement" && imageMeasurementMetric && imageMeasurementMetric !== "grade") {
    return getImageMeasurementValue(entryCollections, entryConfigId, imageMeasurementMetric, entryConfig);
  }

  // For standCountMeasurement types, use the specialized function with metric selection
  if (entryType === "standCountMeasurement" && standCountMetric) {
    return getStandCountValue(entryCollections, entryConfigId, standCountMetric);
  }

  // For rowPlantedStandCount types, use the specialized function with metric selection
  if (entryType === "rowPlantedStandCount" && rowPlantedStandCountMetric) {
    return getRowPlantedStandCountValue(entryCollections, entryConfigId, rowPlantedStandCountMetric);
  }

  // Helper to get all entries from the map
  const getAllEntries = <T>(entriesMap: Record<string, T> | undefined): T[] => {
    if (!entriesMap) return [];
    return Object.values(entriesMap);
  };

  // Helper to calculate average
  const calculateAverage = (values: number[]): number | null => {
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  // Helper to find most recent collection
  const getMostRecentCollection = (): EntryCollection => {
    return matchingCollections.reduce((mostRecent, current) => {
      const mostRecentTime = mostRecent.createdAt?.seconds || 0;
      const currentTime = current.createdAt?.seconds || 0;
      return currentTime > mostRecentTime ? current : mostRecent;
    });
  };

  let value: unknown = null;

  switch (entryType) {
    case "number": {
      // Aggregate all number entries from all collections
      const allValues = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.numberEntries);
        return entries.map((e) => e.val).filter((v) => v != null) as number[];
      });
      value = calculateAverage(allValues);
      break;
    }
    case "percentage": {
      // Aggregate all percentage entries from all collections
      const allValues = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.percentageEntries);
        return entries.map((e) => e.val).filter((v) => v != null) as number[];
      });
      value = calculateAverage(allValues);
      break;
    }
    case "leafAreaIndex": {
      // Aggregate all LAI entries from all collections
      const allValues = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.leafAreaIndexEntries);
        return entries.map((e) => e.val).filter((v) => v != null) as number[];
      });
      value = calculateAverage(allValues);
      break;
    }
    case "ruler": {
      // Aggregate all ruler measurements from all collections
      const allMeasurements = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.rulerEntries);
        return entries.flatMap((e) => e.measurementsMeters || []).filter((m) => m != null) as number[];
      });
      value = calculateAverage(allMeasurements);
      break;
    }
    case "timer": {
      // Timer durations are not aggregated — surface the single most-recent
      // stopped sample across all collections/samples (ranked by entry
      // timestamp). A newer idle/running collection cannot hide an older stopped
      // duration, and a later-stopped sample wins over an earlier one.
      value = getMostRecentStoppedTimerMs(matchingCollections);
      break;
    }
    case "imageMeasurement": {
      // Fallback if no metric specified - sum all counts
      const allValues = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.imageMeasurementEntries);
        return entries.map((e) => e.objectCount).filter((v) => v != null) as number[];
      });
      if (allValues.length === 0) return null;
      value = allValues.reduce((sum, count) => sum + count, 0);
      break;
    }
    case "standCountMeasurement": {
      // Fallback if no metric specified - average countPerSquareMeter
      const allValues = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.standCountMeasurementEntries);
        return entries.map((e) => e.countPerSquareMeter).filter((v) => v != null) as number[];
      });
      value = calculateAverage(allValues);
      break;
    }
    case "rowPlantedStandCount": {
      // Fallback if no metric specified - average plantsPerMeter
      const allValues = matchingCollections.flatMap((collection) => {
        const entries = getAllEntries(collection.rowPlantedStandCountEntries);
        return entries.map((e) => e.plantsPerMeter).filter((v) => v != null) as number[];
      });
      value = calculateAverage(allValues);
      break;
    }
    // Non-numerical types - return most recent value
    case "text":
    case "transcription": {
      const mostRecent = getMostRecentCollection();
      const map = entryType === "text" ? mostRecent.textEntries : mostRecent.transcriptionEntries;
      const entries = Object.values(map || {});
      const firstNonEmpty = entries.find((e) => e?.text != null && e.text !== "");
      value = firstNonEmpty?.text || null;
      break;
    }
    case "category": {
      const mostRecent = getMostRecentCollection();
      const entries = Object.values(mostRecent.categoryEntries || {});
      const firstNonEmpty = entries.find((e) => e?.selections && e.selections.length > 0);
      value = firstNonEmpty?.selections || null;
      break;
    }
    case "date": {
      // Collect all dates from all collections and calculate average
      const allDates = matchingCollections.flatMap((collection) => {
        const entries = Object.values(collection.dateEntries || {});
        return entries
          .map((e) => e?.date)
          .filter((d) => d != null)
          .map((d) => {
            // Handle Firestore Timestamp - convert to milliseconds
            if (d && typeof d === "object" && "seconds" in d) {
              return (d as { seconds: number; nanoseconds: number }).seconds * 1000;
            }
            // Handle Date object
            if (d instanceof Date) {
              return d.getTime();
            }
            return null;
          })
          .filter((ms) => ms != null) as number[];
      });

      if (allDates.length === 0) {
        value = null;
      } else {
        // Calculate average date
        const totalMs = allDates.reduce((sum, ms) => sum + ms, 0);
        const averageMs = totalMs / allDates.length;
        // Return as proper Firestore Timestamp so .toDate() works in formatEntryValue
        value = Timestamp.fromMillis(averageMs);
      }
      break;
    }
    case "boolean": {
      // Search for a sample that HAS a value, not merely a sample that exists —
      // mirrors the text/category/date predicates above. `?? null` rather than
      // `|| null` so a legitimate `false` survives.
      const mostRecent = getMostRecentCollection();
      value = findFirstBooleanEntryWithValue(mostRecent.booleanEntries)?.val ?? null;
      break;
    }
    case "photo": {
      // For photo types, return "photo" sentinel if any photos exist, null otherwise
      const hasPhotos = matchingCollections.some((collection) => {
        const entries = Object.values(collection.photoEntries || {});
        return entries.some((e) => e?.imgCloudPath);
      });
      value = hasPhotos ? "photo" : null;
      break;
    }
    default:
      value = null;
  }

  return value;
}

/**
 * Extracts a display value with status information for metrics that use weighted averaging.
 * Returns AggregatedValueResult with error state when weighted averaging fails.
 * For non-weighted-average cases, returns the value with hasError: false.
 */
export function getEntryDisplayValueWithStatus(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  imageMeasurementMetric?: ImageMeasurementMetric,
  standCountMetric?: StandCountMetric,
  rowPlantedStandCountMetric?: RowPlantedStandCountMetric,
  entryConfig?: EntryConfig
): AggregatedValueResult {
  // Filter EntryCollections to only those matching the selected EntryConfig
  const matchingCollections = entryCollections.filter((entryCollection) => {
    const collectionEntryConfigId =
      typeof entryCollection.entryConfigRef === "string"
        ? entryCollection.entryConfigRef
        : entryCollection.entryConfigRef.id;
    return collectionEntryConfigId === entryConfigId;
  });

  if (matchingCollections.length === 0) {
    return {
      value: null,
      hasError: false,
      totalEntries: 0,
      validWeightedEntries: 0,
    };
  }

  const entryType = matchingCollections[0].type;

  // 'grade' is a categorical metric — it does not return a numeric value through
  // this path. The heat map computes the per-plot dominant grade via a separate
  // code path (see getImageMeasurementGradeDistribution).
  if (entryType === "imageMeasurement" && imageMeasurementMetric === "grade") {
    return {
      value: null,
      hasError: false,
      totalEntries: matchingCollections.flatMap((c) => Object.values(c.imageMeasurementEntries || {})).length,
      validWeightedEntries: 0,
    };
  }

  // For imageMeasurement length/width metrics, use the WithStatus function
  if (entryType === "imageMeasurement" && imageMeasurementMetric && imageMeasurementMetric !== "grade") {
    if (
      imageMeasurementMetric === "length" ||
      imageMeasurementMetric === "width" ||
      getImageMeasurementDescriptor(entryConfig, imageMeasurementMetric)
    ) {
      return getImageMeasurementValueWithStatus(entryCollections, entryConfigId, imageMeasurementMetric, entryConfig);
    }
    // count/weight don't need weighted averaging, use original
    const value = getImageMeasurementValue(entryCollections, entryConfigId, imageMeasurementMetric, entryConfig);
    return {
      value,
      hasError: false,
      totalEntries: matchingCollections.flatMap((c) => Object.values(c.imageMeasurementEntries || {})).length,
      validWeightedEntries: 0,
    };
  }

  // For standCountMeasurement avgLength/avgWidth metrics, use the WithStatus function
  if (entryType === "standCountMeasurement" && standCountMetric) {
    if (standCountMetric === "avgLength" || standCountMetric === "avgWidth") {
      return getStandCountValueWithStatus(entryCollections, entryConfigId, standCountMetric);
    }
    // countPerM2/rawCount/totalWeight don't need weighted averaging, use original
    const value = getStandCountValue(entryCollections, entryConfigId, standCountMetric);
    return {
      value,
      hasError: false,
      totalEntries: matchingCollections.flatMap((c) => Object.values(c.standCountMeasurementEntries || {})).length,
      validWeightedEntries: 0,
    };
  }

  // For rowPlantedStandCount averaging metrics, use the WithStatus function
  if (entryType === "rowPlantedStandCount" && rowPlantedStandCountMetric) {
    if (
      rowPlantedStandCountMetric === "averageSpacing" ||
      rowPlantedStandCountMetric === "averageDiameter" ||
      rowPlantedStandCountMetric === "averageWidth" ||
      rowPlantedStandCountMetric === "averageGreenness"
    ) {
      return getRowPlantedStandCountValueWithStatus(entryCollections, entryConfigId, rowPlantedStandCountMetric);
    }
    // plantsPerMeter doesn't need the same weighted averaging error handling
    const value = getRowPlantedStandCountValue(entryCollections, entryConfigId, rowPlantedStandCountMetric);
    return {
      value,
      hasError: false,
      totalEntries: matchingCollections.flatMap((c) => Object.values(c.rowPlantedStandCountEntries || {})).length,
      validWeightedEntries: 0,
    };
  }

  // For all other types, just use getEntryDisplayValue (no weighted averaging needed)
  const value = getEntryDisplayValue(
    entryCollections,
    entryConfigId,
    imageMeasurementMetric,
    standCountMetric,
    rowPlantedStandCountMetric,
    entryConfig
  );

  return {
    value,
    hasError: false,
    totalEntries: matchingCollections.length,
    validWeightedEntries: 0,
  };
}

/**
 * Extracts all individual values from a single EntryCollection.
 * For numerical entry types, returns an array of all values.
 * For non-numerical entry types, returns an array with the first value.
 */
export function getAllEntryValues(
  entryCollection: EntryCollection,
  imageMeasurementMetric?: ImageMeasurementMetric,
  standCountMetric?: StandCountMetric,
  rowPlantedStandCountMetric?: RowPlantedStandCountMetric,
  entryConfig?: EntryConfig
): number[] {
  // Helper to get all entries from the map
  const getAllEntries = <T>(entriesMap: Record<string, T> | undefined): T[] => {
    if (!entriesMap) return [];
    return Object.values(entriesMap);
  };

  let values: number[] = [];

  switch (entryCollection.type) {
    case "number": {
      const entries = getAllEntries(entryCollection.numberEntries);
      values = entries.map((e) => e.val).filter((v) => v != null) as number[];
      break;
    }
    case "percentage": {
      const entries = getAllEntries(entryCollection.percentageEntries);
      values = entries.map((e) => e.val).filter((v) => v != null) as number[];
      break;
    }
    case "leafAreaIndex": {
      const entries = getAllEntries(entryCollection.leafAreaIndexEntries);
      values = entries.map((e) => e.val).filter((v) => v != null) as number[];
      break;
    }
    case "ruler": {
      const entries = getAllEntries(entryCollection.rulerEntries);
      values = entries.flatMap((e) => e.measurementsMeters || []).filter((m) => m != null) as number[];
      break;
    }
    case "timer": {
      // Raw elapsed duration in ms per stopped sample (idle/running skipped).
      const entries = getAllEntries(entryCollection.timerEntries);
      values = entries.map(getStoppedTimerMs).filter((ms): ms is number => ms != null);
      break;
    }
    case "imageMeasurement": {
      const entries = getAllEntries(entryCollection.imageMeasurementEntries);
      const metric = imageMeasurementMetric || "count";

      switch (metric) {
        case "grade":
          // Grade is categorical and computed from per-object CaptureMetadata,
          // not from entry-level scalar fields. Return empty so callers that
          // expect numeric per-sample dots simply skip this metric.
          values = [];
          break;
        case "length":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.avgLengthMeters != null ? e.avgLengthMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "width":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.avgWidthMeters != null ? e.avgWidthMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "count":
          values = entries.map((e) => e.objectCount).filter((v) => v != null) as number[];
          break;
        case "weight":
          values = entries.map((e) => e.totalWeightGrams).filter((v) => v != null) as number[];
          break;
        default: {
          const descriptor = getImageMeasurementDescriptor(entryConfig, metric);
          values = descriptor
            ? entries
                .map((entry) => getDescriptorMeasurementValue(entry, descriptor))
                .filter((value): value is number => value != null)
            : [];
          break;
        }
      }
      break;
    }
    case "standCountMeasurement": {
      const entries = getAllEntries(entryCollection.standCountMeasurementEntries);
      const metric = standCountMetric || "countPerM2";

      switch (metric) {
        case "countPerM2":
          values = entries.map((e) => e.countPerSquareMeter).filter((v) => v != null) as number[];
          break;
        case "rawCount":
          values = entries.map((e) => e.imageMeasurement?.objectCount).filter((v) => v != null) as number[];
          break;
        case "avgLength":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.imageMeasurement?.avgLengthMeters != null ? e.imageMeasurement.avgLengthMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "avgWidth":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.imageMeasurement?.avgWidthMeters != null ? e.imageMeasurement.avgWidthMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "totalWeight":
          values = entries.map((e) => e.imageMeasurement?.totalWeightGrams).filter((v) => v != null) as number[];
          break;
      }
      break;
    }
    case "rowPlantedStandCount": {
      const entries = getAllEntries(entryCollection.rowPlantedStandCountEntries);
      const metric = rowPlantedStandCountMetric || "plantsPerMeter";

      switch (metric) {
        case "plantsPerMeter":
          values = entries.map((e) => e.plantsPerMeter).filter((v) => v != null) as number[];
          break;
        case "numberPlants":
          values = entries.map((e) => e.plantCount).filter((v) => v != null) as number[];
          break;
        case "averageSpacing":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.averageSpacingMeters != null ? e.averageSpacingMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "averageDiameter":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.averageDiameterMeters != null ? e.averageDiameterMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "averageWidth":
          // Convert meters to centimeters (multiply by 100)
          values = entries
            .map((e) => (e.averageWidthMeters != null ? e.averageWidthMeters * 100 : null))
            .filter((v) => v != null) as number[];
          break;
        case "averageGreenness":
          values = entries.map((e) => e.averageVari).filter((v) => v != null) as number[];
          break;
      }
      break;
    }
    // Non-numerical types - return first value as a single-element array
    case "boolean": {
      // Find the first sample that HAS a value, so this agrees with the boolean
      // case in getEntryDisplayValue rather than reporting "no value" for a plot
      // whose answer sits behind an empty-but-present slot.
      //
      // Defensive, not a live fix: the only production caller
      // (D3HeatmapGrid `getSampleValues`) returns early for boolean traits because
      // getDefaultMetric() yields no metric for them, so this branch is currently
      // reachable only from unit tests. Keeping the two predicates aligned means
      // giving boolean a metric selector later can't silently reintroduce GLE-84.
      const val = findFirstBooleanEntryWithValue(entryCollection.booleanEntries)?.val;
      if (val != null) values = [val ? 1 : 0];
      break;
    }
  }

  return values;
}
